import type { Messages } from './messages';

/**
 * Copy for `src/app/tenant/overview/`. Owned by that page's author, and by nobody else.
 *
 * Every key starts with `tenantOverview.` — the compiler refuses anything else, so
 * this file cannot collide with another page's. Shared words (save, cancel,
 * retry, statuses, role names) are already in `common.ts` and
 * `vocabulary.ts`; use those rather than restating them here.
 */
export const tenantOverview = {
  /* Sections */
  'tenantOverview.sections': 'أقسام النظرة العامة',
  'tenantOverview.section.people': 'الأعضاء',
  'tenantOverview.section.spend': 'الإنفاق والنماذج',
  'tenantOverview.section.content': 'المحتوى',
  'tenantOverview.section.activity': 'النشاط',
  'tenantOverview.section.trends': 'النشاط الأسبوعي',
  'tenantOverview.section.identity': 'الهوية والروابط',
  'tenantOverview.section.flag': 'فيه ما يحتاج انتباهًا',

  'tenantOverview.stale': 'تعذّر تحديث الأرقام؛ المعروض آخر ما حُمِّل.',

  /* What needs attention — first on the page, because it is why somebody opened it. */
  'tenantOverview.noAdminTitle': 'لا مشرف عامّ نشط في هذه المنشأة',
  'tenantOverview.noAdminBody':
    'لا أحد داخل المنشأة يستطيع الآن إدارة أعضائها أو أدوارها أو نماذجها. فعّل حساب مشرف عام موقوفًا أو بانتظار التفعيل، أو امنح أحد الأعضاء هذا الدور.',
  'tenantOverview.noAdminAction': 'فتح الأعضاء',
  'tenantOverview.noModelsTitle': 'لا نموذج مفعّل',
  'tenantOverview.noModelsBody': 'لن تُجاب أسئلة أعضاء المنشأة حتى يُفعَّل نموذج واحد على الأقل.',
  'tenantOverview.noModelsAction': 'فتح النماذج',
  'tenantOverview.overAllocatedTitle': 'حدود الأعضاء تتجاوز سقف المنشأة',
  'tenantOverview.overAllocatedBody':
    'وزّعت المنشأة على أعضائها {allocated} شهريًا وسقفها {cap}، فسيبلغ بعضهم السقف المشترك قبل حدّه الشخصي. الإعداد عند إدارة المنشأة نفسها.',
  'tenantOverview.failedTitle': 'مستندات متعثّرة: {count}',
  'tenantOverview.failedBody':
    'لم تكتمل فهرستها، فلا تدخل في الإجابات. أسماؤها غير مقروءة من هنا؛ اتجاهها في قسم «النشاط الأسبوعي»، والأحداث في التدقيق.',
  'tenantOverview.toActivity': 'عرض الاتجاه الأسبوعي',
  'tenantOverview.toAudit': 'فتح التدقيق',

  /* Accounts */
  'tenantOverview.accountsActive': 'الحسابات النشطة',
  'tenantOverview.accountsActiveSub': 'من أصل {total}',
  'tenantOverview.admins': 'المشرفون العامّون النشطون',
  'tenantOverview.adminsSub': 'يديرون الأعضاء والأدوار والنماذج',
  'tenantOverview.adminsNoneSub': 'لا أحد يدير المنشأة من داخلها',
  'tenantOverview.reviewers': 'أصحاب صلاحية مراجعة المحادثات',
  'tenantOverview.reviewersSub': 'تُمنح فردًا فردًا وتفتح لحاملها محادثات زملائه',
  'tenantOverview.reviewersNoneSub': 'لا أحد يقرأ محادثات زملائه',
  'tenantOverview.byStatus': 'الحسابات حسب الحالة',

  /* Notifications — counts and kind codes only */
  'tenantOverview.notifications.title': 'الإشعارات',
  'tenantOverview.notifications.description':
    'ما أُرسل إلى أعضاء المنشأة داخل المنصّة، للإجابة عن «لم يصلني إشعار». أعداد وأنواع فقط.',
  'tenantOverview.notifications.boundary': 'لا يُقرأ من هنا عنوان إشعار ولا القضية التي يخصّها ولا من أُرسل إليه.',
  'tenantOverview.notifications.sent7d': 'أُرسل في 7 أيام',
  'tenantOverview.notifications.sent30d': 'أُرسل في 30 يومًا',
  'tenantOverview.notifications.unread': 'غير مقروءة',
  'tenantOverview.notifications.unreadSub': 'منذ البداية، عند كل المستلمين',
  'tenantOverview.notifications.byKind': 'آخر 30 يومًا حسب النوع',
  'tenantOverview.notifications.byKindEmpty': 'لم يُرسل إشعار في آخر 30 يومًا.',
  'tenantOverview.notifications.quietTitle': 'لا إشعارات في آخر 30 يومًا',
  'tenantOverview.notifications.quietBody':
    'تُرسل الإشعارات حين يُسنَد عضو إلى قضية أو يُزال من فريقها. إن قال عضو إن إشعارًا لم يصله، فتحقّق أولًا أنه أُضيف إلى فريق القضية من داخل المنشأة.',
  'tenantOverview.notifications.kind.CASE_ASSIGNED': 'إسناد إلى قضية',
  'tenantOverview.notifications.kind.CASE_UNASSIGNED': 'إزالة من فريق قضية',
  'tenantOverview.notifications.kind.OTHER': 'أخرى',
  'tenantOverview.byStatusLabel': 'توزيع حسابات المنشأة حسب الحالة',
  'tenantOverview.byStatusCenter': 'حسابًا',
  'tenantOverview.statusPending': 'طلب انضمام معلّق',
  'tenantOverview.accountsEmptyTitle': 'لا حسابات بعد',
  'tenantOverview.accountsEmptyBody': 'أنشئ حساب المشرف العام الأول من تبويب الأعضاء ليبدأ أعضاء المنشأة الدخول.',

  /* Spend */
  'tenantOverview.spendTitle': 'الإنفاق هذا الشهر',
  'tenantOverview.spendDescription': 'الشهر بتوقيت الرياض، كما يُحتسب للفوترة.',
  'tenantOverview.spendMeter': 'الإنفاق مقابل سقف المنشأة',
  'tenantOverview.spendUsed': 'المستهلك',
  'tenantOverview.spendCap': 'سقف المنشأة',
  'tenantOverview.spendNoCapHint': 'لا شيء يوقف الإنفاق على مستوى المنشأة.',
  'tenantOverview.spendAllocated': 'الموزّع على الأعضاء',
  'tenantOverview.spendAllocatedHint': 'مجموع الحدود الشهرية للأعضاء الذين لهم حدّ.',
  'tenantOverview.overCap': 'أعلى من السقف',
  'tenantOverview.toModels': 'النماذج والإنفاق',

  /* Content — counts only, beside the boundary note */
  'tenantOverview.contentTitle': 'المحتوى والفهرسة',
  'tenantOverview.conversations': 'المحادثات',
  'tenantOverview.conversationsSub': 'منذ إنشاء المنشأة',
  'tenantOverview.casesActive': 'القضايا النشطة',
  'tenantOverview.casesActiveSub': 'غير المغلقة',
  'tenantOverview.projects': 'المشاريع',
  'tenantOverview.projectsSub': 'منذ إنشاء المنشأة',
  'tenantOverview.documentsReady': 'المستندات الجاهزة',
  'tenantOverview.documentsReadySub': 'من أصل {total}',
  'tenantOverview.documentsFailed': 'المستندات المتعثّرة',
  'tenantOverview.documentsFailedSub': 'لم تكتمل فهرستها — عرض الاتجاه',
  'tenantOverview.documentsFailedNone': 'لا شيء متعثّر',
  'tenantOverview.models': 'النماذج المفعّلة',
  'tenantOverview.modelsSub': 'المتاحة لأعضاء المنشأة',
  'tenantOverview.modelsNoneSub': 'لا يستطيع الأعضاء السؤال',

  /* Ingestion */
  'tenantOverview.ingestionTitle': 'صحة الفهرسة',
  'tenantOverview.ingestion.empty': 'لا مستندات بعد',
  'tenantOverview.ingestion.healthy': 'تعمل',
  'tenantOverview.ingestion.processing': 'قيد المعالجة',
  'tenantOverview.ingestion.failing': 'فيها تعثّر',
  'tenantOverview.ingestion.stalled': 'متوقفة على ما يبدو',
  'tenantOverview.ingestionBody.empty': 'لم يرفع أعضاء المنشأة أي مستند بعد.',
  'tenantOverview.ingestionBody.healthy': 'كل المستندات المرفوعة جاهزة للبحث.',
  'tenantOverview.ingestionBody.processing': 'مستندات قيد الفهرسة الآن: {count}. لا تعثّر.',
  'tenantOverview.ingestionBody.failing': 'تعثّر {failed} من {total} ({share}).',
  'tenantOverview.ingestionBody.stalled':
    'رُفع {total} ولم يجهز أيٌّ منها. قد تكون الفهرسة متوقفة — راجع حالة العامل قبل أن تبلغك المنشأة.',
  'tenantOverview.ingestionReady': 'جاهزة',
  'tenantOverview.ingestionProcessing': 'قيد المعالجة',
  'tenantOverview.ingestionFailed': 'متعثّرة',

  /* Identity */
  'tenantOverview.identityTitle': 'هوية المنشأة',
  'tenantOverview.name': 'الاسم',
  'tenantOverview.slug': 'المعرّف النصّي',
  'tenantOverview.slugHint': 'جزء من عنوان دخول الأعضاء، ولا يتغيّر بعد الإنشاء.',
  'tenantOverview.id': 'المعرّف',
  'tenantOverview.vertical': 'القطاع',
  'tenantOverview.status': 'الحالة',
  'tenantOverview.created': 'تاريخ الإنشاء',
  'tenantOverview.frontendUrl': 'عنوان الواجهة',
  'tenantOverview.frontendDefault': 'الافتراضي للتنصيب',
  'tenantOverview.lastSeen': 'آخر نشاط لأحد أعضائها',

  /* Quick links */
  'tenantOverview.linksTitle': 'إعدادات المنشأة',
  'tenantOverview.linksDescription': 'ما يُضبط لهذه المنشأة، كلٌّ في تبويبه.',
  'tenantOverview.linkMail': 'خادم البريد والرسائل المرسلة',
  'tenantOverview.linkMicrosoft': 'الدليل والنطاقات المسموح بها',
  'tenantOverview.linkTemplatesLabel': 'قوالب البريد',
  'tenantOverview.linkTemplates': 'نصوص الرسائل وهويتها',

  /* ── Activity sections (was «النشاط»; merged 2026-10-08) ────────────────── */
  'tenantOverview.activity.stale': 'تعذّر تحديث النشاط؛ المعروض آخر ما حُمِّل.',

  /* The period */
  'tenantOverview.activity.period': 'الفترة',
  'tenantOverview.activity.weeks.4': '4 أسابيع',
  'tenantOverview.activity.weeks.12': '12 أسبوعًا',
  'tenantOverview.activity.weeks.26': '26 أسبوعًا',
  'tenantOverview.activity.weekNote': 'الأسبوع يبدأ يوم الاثنين بتوقيت الرياض. حُسبت {time}.',

  /* Totals — since the المنشأة was created, not in the period */
  'tenantOverview.activity.totalsTitle': 'الإجمالي منذ الإنشاء',
  'tenantOverview.activity.inPeriod': 'في الفترة: {count}',
  'tenantOverview.activity.total.conversations': 'المحادثات',
  'tenantOverview.activity.total.cases': 'القضايا',
  'tenantOverview.activity.total.projects': 'المشاريع',
  'tenantOverview.activity.total.documents': 'المستندات',
  'tenantOverview.activity.total.documentsFailed': 'المستندات المتعثّرة',
  'tenantOverview.activity.total.messagesSent': 'أسئلة الأعضاء',

  /* The weekly series */
  'tenantOverview.activity.trendsTitle': 'كل أسبوع',
  'tenantOverview.activity.series.conversationsCreated': 'محادثات جديدة',
  'tenantOverview.activity.series.casesCreated': 'قضايا جديدة',
  'tenantOverview.activity.series.projectsCreated': 'مشاريع جديدة',
  'tenantOverview.activity.series.documentsAdded': 'مستندات مضافة',
  'tenantOverview.activity.series.documentsFailed': 'مستندات متعثّرة',
  'tenantOverview.activity.series.messagesSent': 'أسئلة الأعضاء',
  'tenantOverview.activity.chartLabel': '{series} لكل أسبوع، آخر {weeks}',
  'tenantOverview.activity.trendLabel': '{series} خلال الفترة',
  'tenantOverview.activity.failedOfAdded': 'منها {failed} متعثّرة',
  'tenantOverview.activity.messagesNote': 'أسئلة الأعضاء وحدها، لا إجابات النموذج.',
  'tenantOverview.activity.failedNote': 'بحالتها الآن: مستند أُضيف في أسبوع وتعثّر لاحقًا يُحسب في أسبوع إضافته.',
  'tenantOverview.activity.peak': 'الأعلى: {count} في أسبوع {day}.',
  'tenantOverview.activity.quiet': 'لا شيء في هذه الفترة. جرّب فترة أطول.',
  'tenantOverview.activity.quietLongest': 'لا شيء طوال الأسابيع الستة والعشرين الماضية — أطول فترة يعرضها هذا القسم.',

  /* Before the endpoint exists */
  'tenantOverview.activity.unavailableTitle': 'الاتجاهات الأسبوعية غير متاحة بعد',
  'tenantOverview.activity.unavailableBody':
    'لا يقدّم الخادم بعد سلسلة أسبوعية لهذه المنشأة. أعدادها الحالية في قسم «المحتوى»، وستظهر الاتجاهات هنا حين يُضاف ذلك.',
} as const satisfies Messages<'tenantOverview'>;
